import DeforestationService from '@/_services/DeforestationService'
import s3UploadService from '@/_services/S3UploadService'
import { throwOnGraphqlErrors } from '@/utils/ddsMitigationNotification'
import { DDS_MITIGATION_SOURCE_TYPE, normalizeMitigationSourceType } from '@/utils/ddsMitigationSourceType'
import {
  calculateFarmTrafficLight,
  normalizeCommodity,
  trafficLightColorFromState,
  trafficLightIconFromState,
  trafficLightLabelFromState,
  trafficLightFromDeforestationStatus,
} from '@/utils/trafficLightCalculator'

export function getFarmTrafficLight(item) {
  if (item?.computedTrafficLight) return item.computedTrafficLight
  return calculateFarmTrafficLight({
    item,
    iybData: item?.iybData,
    phase1: item?.phase1,
    commodity: item?.commodity,
  })
}

export function isMitigationReviewFarm(item, { hasDocs = false } = {}) {
  if (hasDocs) return true
  const tl = getFarmTrafficLight(item)
  return tl === 'YELLOW' || tl === 'LIGHT_RED' || tl === 'DARK_RED'
}

export function isFarmMediumRisk(item) {
  return getFarmTrafficLight(item) === 'YELLOW'
}

export function isFarmHighRisk(item) {
  const tl = getFarmTrafficLight(item)
  return tl === 'LIGHT_RED' || tl === 'DARK_RED'
}

export function getFarmMitigationFiles(item) {
  if (!item) return []
  const direct = Array.isArray(item.risk_mitigation_files) ? item.risk_mitigation_files : []
  const nested = (item.diligenceReportProductionPlaceArray || []).flatMap((reportPlace) => {
    const reportLevel = Array.isArray(reportPlace?.riskMitigationFiles) ? reportPlace.riskMitigationFiles : []
    const infoLevel = Array.isArray(reportPlace?.productionPlaceDeforestationInfo?.riskMitigationFiles)
      ? reportPlace.productionPlaceDeforestationInfo.riskMitigationFiles
      : []
    return [...reportLevel, ...infoLevel]
  })
  const manual = (item.dueDiligenceProductionPlacesPyData?.dueDiligenceProductionManuallyMitigated || [])
    .map((entry) => ({
      file_path: entry.riskMitigationFile,
      riskMitigationComment: entry.riskMitigationComment,
      id: entry.id,
      isManualMitigation: true,
    }))

  const allFiles = [...direct, ...nested, ...manual]
  const normalized = allFiles
    .map((file) => {
      if (!file) return null
      const filePath = file.file_path || file.riskMitigationFile || file.path || null
      if (!filePath) return null
      return {
        file_path: filePath,
        riskMitigationComment: file.riskMitigationComment || file.risk_mitigation_comment || '',
        mitigation_source_type: normalizeMitigationSourceType(file.mitigation_source_type),
        id: file.id || file.riskMitigationFileId || file.fileId || filePath,
        isManualMitigation: Boolean(file.isManualMitigation),
      }
    })
    .filter(Boolean)

  return [...new Map(normalized.map((file) => [file.file_path, file])).values()]
}

/**
 * Traffic light assessment UI — used on the Compliance Status Review step.
 */
export default {
  data() {
    return {
      trafficLightDialog: false,
      trafficLightItem: null,
      trafficLightMapKey: 0,
      reportCommodity: null,
      nextConfirmDialog: false,
      nextConfirmResolve: null,
      bulkActionLoading: false,
      removeConfirmDialog: { open: false, type: '' },
      attachDocDialog: { open: false, file: null, selectedScopes: [], loading: false },
      mitigatedFarmIds: [],
      trafficLightSummary: null,
      singleFarmAttachDialog: {
        open: false,
        file: null,
        farmItem: null,
        existingFiles: [],
        loading: false,
        revisionComment: '',
        uploadProgress: 0,
        loadingMessage: '',
      },
      yellowWarningDialog: { open: false, farmItem: null },
    }
  },
  computed: {
    trafficLightPlaces() {
      return this.productionPlaces || []
    },
    reportTrafficLightFarms() {
      return this.trafficLightSummary?.trafficLightFarms || []
    },
    darkRedFarmCount() {
      if (this.trafficLightSummary) return this.trafficLightSummary.darkRedCount
      return this.darkRedFarms.length
    },
    lightRedFarmCount() {
      if (this.trafficLightSummary) return this.trafficLightSummary.lightRedCount
      return this.trafficLightPlaces.filter(item => this.getTrafficLight(item) === 'LIGHT_RED').length
    },
    darkRedFarms() {
      if (this.reportTrafficLightFarms.length) {
        return this.summaryFarmsToPlaceItems(
          this.reportTrafficLightFarms.filter((farm) => farm.trafficLight === 'DARK_RED'),
        )
      }
      return this.trafficLightPlaces.filter(item => this.getTrafficLight(item) === 'DARK_RED')
    },
    lightRedFarms() {
      if (this.reportTrafficLightFarms.length) {
        return this.summaryFarmsToPlaceItems(
          this.reportTrafficLightFarms.filter((farm) => farm.trafficLight === 'LIGHT_RED'),
        )
      }
      return this.trafficLightPlaces.filter(item => this.getTrafficLight(item) === 'LIGHT_RED')
    },
    unmitigatedLightRedCount() {
      if (this.trafficLightSummary) return this.trafficLightSummary.unmitigatedLightRedCount
      return this.lightRedFarms.filter(item => !this.mitigatedFarmIds.includes(item.id)).length
    },
    yellowFarmCount() {
      if (this.trafficLightSummary) return this.trafficLightSummary.yellowCount
      return this.yellowFarms.length
    },
    yellowFarms() {
      if (this.reportTrafficLightFarms.length) {
        return this.summaryFarmsToPlaceItems(
          this.reportTrafficLightFarms.filter((farm) => farm.trafficLight === 'YELLOW'),
        )
      }
      return this.trafficLightPlaces.filter(item => this.getTrafficLight(item) === 'YELLOW')
    },
    unmitigatedYellowCount() {
      if (this.trafficLightSummary) return this.trafficLightSummary.unmitigatedYellowCount
      return this.yellowFarms.filter(item => !this.mitigatedFarmIds.includes(item.id)).length
    },
    hasPendingTrafficLightIssues() {
      return this.darkRedFarmCount > 0
        || this.unmitigatedLightRedCount > 0
        || this.unmitigatedYellowCount > 0
    },
    canProceedFromTrafficLightStep() {
      if (this.trafficLightSummary) return this.trafficLightSummary.canProceed
      return this.darkRedFarmCount === 0 && this.unmitigatedLightRedCount === 0
    },
    blocksTrafficLightProceed() {
      return !this.canProceedFromTrafficLightStep
    },
    tlDialogColor() {
      const tl = this.trafficLightItem ? this.getTrafficLight(this.trafficLightItem) : null
      return trafficLightColorFromState(tl)
    },
    tlDialogHeaderStyle() {
      const tl = this.trafficLightItem ? this.getTrafficLight(this.trafficLightItem) : null
      if (tl === 'GREEN') return 'background:#f4fff4; border-bottom:2px solid #C1FFE7;'
      if (tl === 'YELLOW') return 'background:#fffdf4; border-bottom:2px solid #FFD980;'
      if (tl === 'LIGHT_RED') return 'background:#fff5f5; border-bottom:2px solid #FFBBBB;'
      if (tl === 'DARK_RED') return 'background:#fdf0f0; border-bottom:2px solid #CC0000;'
      return 'background:#fafafa; border-bottom:2px solid #e0e0e0;'
    },
  },
  methods: {
    mapTrafficLightToMitigationStatus(item) {
      const tl = this.getTrafficLight(item)
      if (tl === 'DARK_RED') return 'Very High Deforestation Probability'
      if (tl === 'LIGHT_RED') return 'High Deforestation Probability'
      if (tl === 'YELLOW') return 'Medium Deforestation Probability'
      return 'Manually Mitigated'
    },
    getReportProductionPlaceId(item) {
      const reportPlaceId = item?.diligenceReportProductionPlaceArray?.[0]?.id
      if (reportPlaceId != null) return Number(reportPlaceId)
      return Number(item?.id)
    },
    async uploadMitigationFile(file) {
      if (!file) return null
      const folder = 'risk_mitigation_files'
      const result = await s3UploadService.singleUpload(file, folder)
      return result?.fullPath || null
    },
    getFarmMitigationFiles(item) {
      return getFarmMitigationFiles(item)
    },
    getMitigationFileName(filePath) {
      if (!filePath) return 'Mitigation document'
      const parts = String(filePath).split('/')
      return parts[parts.length - 1] || 'Mitigation document'
    },
    canRemoveMitigationFile(file) {
      if (!file) return false
      const fileId = Number(file.id)
      return Number.isInteger(fileId) && fileId > 0
    },
    async removeSingleFarmMitigationDoc(file) {
      if (!this.canRemoveMitigationFile(file)) return

      const confirmed = window.confirm('Are you sure you want to remove this mitigation document?')
      if (!confirmed) return

      this.singleFarmAttachDialog.loading = true
      try {
        const farmItem = this.singleFarmAttachDialog.farmItem
        if (file.isManualMitigation) {
          await DeforestationService.removeManuallyMitigatedFile({ fileId: Number(file.id) })
        } else {
          await DeforestationService.removeRiskMitigationFile({
            productionPlaceId: Number(farmItem.id),
            fileId: Number(file.id),
          })
        }

        await this.getProductionPlaces()
        const refreshedItem = (this.productionPlaces || []).find((row) => row.id === farmItem.id)
        this.singleFarmAttachDialog.farmItem = refreshedItem || farmItem
        this.singleFarmAttachDialog.existingFiles = this.getFarmMitigationFiles(
          this.singleFarmAttachDialog.farmItem,
        )
        this.$notify({ text: 'Mitigation document removed successfully.', type: 'success' })
      } catch (error) {
        console.error('Failed to remove mitigation document', error)
        this.$notify({ text: error?.message || this.$t('somethingWentWrong'), type: 'error' })
      } finally {
        this.singleFarmAttachDialog.loading = false
      }
    },
    async openMitigationFile(filePath) {
      if (!filePath) return
      try {
        const fileKey = filePath.split('.com/')[1] || filePath
        const viewUrl = await s3UploadService.getFileUrl(fileKey)
        window.open(viewUrl, '_blank')
      } catch (error) {
        console.error('Error opening mitigation file', error)
        this.$notify({ text: this.$t('somethingWentWrong'), type: 'error' })
      }
    },
    summaryFarmsToPlaceItems(summaryFarms = []) {
      return summaryFarms.map((farm) => ({
        id: farm.productionPlaceId,
        computedTrafficLight: farm.trafficLight,
        farm: {
          id: farm.farmId,
          farmName: farm.farmName,
        },
        diligenceReportProductionPlaceArray: [{
          id: farm.diligenceReportProductionPlaceId,
        }],
      }))
    },
    async refreshTrafficLightSummary() {
      if (!this.diligenceId) return null
      try {
        const res = await DeforestationService.getDiligenceReportTrafficLightSummary(this.diligenceId)
        this.trafficLightSummary = res?.data?.diligenceReportTrafficLightSummary || null
        if (this.trafficLightSummary?.mitigatedProductionPlaceIds) {
          this.mitigatedFarmIds = [...this.trafficLightSummary.mitigatedProductionPlaceIds]
        }
        return this.trafficLightSummary
      } catch (error) {
        console.error('Failed to refresh traffic light summary', error)
        return null
      }
    },
    async loadReportCommodity() {
      if (!this.diligenceId) return null
      try {
        const res = await DeforestationService.getDiligenceDetail(this.diligenceId)
        const detail = res?.data?.getDiligenceDetail || {}
        const product =
          detail?.product_detail?.name
          || detail?.product
          || detail?.productCommonName
          || detail?.subProduct
        this.reportCommodity = normalizeCommodity(product)
        return this.reportCommodity
      } catch (error) {
        console.error('Failed to load report commodity', error)
        return null
      }
    },
    openAttachDocDialog() {
      const scopes = []
      if (this.unmitigatedLightRedCount > 0) scopes.push('lightRed')
      if (this.unmitigatedYellowCount > 0) scopes.push('yellow')
      if (!scopes.length) {
        if (this.lightRedFarmCount > 0) scopes.push('lightRed')
        if (this.yellowFarmCount > 0) scopes.push('yellow')
      }
      this.attachDocDialog = { ...this.attachDocDialog, open: true, selectedScopes: scopes, file: null }
    },
    trafficLightLabel(item) {
      return trafficLightLabelFromState(this.getTrafficLight(item), (key) => this.$t(key))
    },
    trafficLightBadgeClass(item) {
      const tl = this.getTrafficLight(item)
      if (tl === 'GREEN') return 'green-button'
      if (tl === 'YELLOW') return 'orange-button'
      if (tl === 'LIGHT_RED' || tl === 'DARK_RED') return 'red-button'
      return 'gray-button'
    },
    openTrafficLightDialog(item) {
      const latest = (this.productionPlaces || []).find((row) => row.id === item.id) || item
      this.trafficLightItem = {
        ...latest,
        farmType: latest.farmType || (latest.farm?.GeoFences?.length ? 'Polygon' : 'point'),
      }
      this.trafficLightMapKey += 1
      this.trafficLightDialog = true
    },
    refreshTrafficLightDialogItem(farmId) {
      if (!this.trafficLightItem || Number(this.trafficLightItem.id) !== Number(farmId)) return
      const refreshedItem = (this.productionPlaces || []).find((row) => row.id === farmId)
      if (!refreshedItem) return
      this.trafficLightItem = {
        ...refreshedItem,
        farmType: this.trafficLightItem.farmType || (refreshedItem.farm?.GeoFences?.length ? 'Polygon' : 'point'),
      }
      this.trafficLightMapKey += 1
    },
    getTrafficLight(item) {
      return getFarmTrafficLight(item)
    },
    trafficLightFromItem(item) {
      return trafficLightFromDeforestationStatus(item)
    },
    trafficLightForPanel(item) {
      const tl = this.getTrafficLight(item)
      if (tl === 'DARK_RED' || tl === 'LIGHT_RED') return 'RED'
      return tl
    },
    demoTrafficLightColor(item) {
      return trafficLightColorFromState(this.getTrafficLight(item))
    },
    demoTrafficLightIcon(item) {
      return trafficLightIconFromState(this.getTrafficLight(item))
    },
    confirmRemoveFarms(type) {
      this.removeConfirmDialog.type = type
      this.removeConfirmDialog.open = true
    },
    async executeRemoveFarms() {
      const type = this.removeConfirmDialog.type
      this.bulkActionLoading = type
      let farms
      if (type === 'darkRed') farms = this.darkRedFarms
      else if (type === 'lightRed') farms = this.lightRedFarms
      else farms = this.yellowFarms
      let successCount = 0
      for (const item of farms) {
        try {
          await DeforestationService.removeFarmFromProductionPlace(
            Number(this.diligenceId), Number(item.farmId)
          )
          successCount++
        } catch (e) {
          console.error(e)
        }
      }
      this.bulkActionLoading = false
      this.removeConfirmDialog.open = false
      if (successCount > 0) {
        if (typeof this.getProductionPlaces === 'function') {
          await this.getProductionPlaces()
        }
        const label = type === 'darkRed' ? 'critical-risk' : type === 'lightRed' ? 'high-risk' : 'medium-risk'
        this.$notify({ text: `${successCount} ${label} farm(s) removed successfully.`, type: 'success' })
      }
    },
    openSingleFarmMitigation(item, { revisionComment = '' } = {}) {
      this.singleFarmAttachDialog = {
        open: true,
        file: null,
        farmItem: item,
        existingFiles: this.getFarmMitigationFiles(item),
        loading: false,
        revisionComment,
      }
    },
    async submitSingleFarmMitigationDoc() {
      if (!this.singleFarmAttachDialog.file) return
      this.singleFarmAttachDialog.loading = true
      try {
        const farmItem = this.singleFarmAttachDialog.farmItem
        const filePath = await this.uploadMitigationFile(this.singleFarmAttachDialog.file)
        if (!filePath) throw new Error('Mitigation file upload failed')

        await DeforestationService.productionPlaceRiskMitigation({
          id: this.getReportProductionPlaceId(farmItem),
          eudr_deforestation_status: this.mapTrafficLightToMitigationStatus(farmItem),
          risk_mitigation_comment: '',
          files: [filePath],
        })

        await this.getProductionPlaces()
        const refreshedItem = (this.productionPlaces || []).find((row) => row.id === farmItem.id)
        this.singleFarmAttachDialog.existingFiles = this.getFarmMitigationFiles(refreshedItem || farmItem)
        this.singleFarmAttachDialog.open = false
        this.$notify({ text: 'Mitigation document uploaded successfully.', type: 'success' })
      } catch (error) {
        console.error('Failed to upload mitigation document', error)
        this.$notify({ text: this.$t('somethingWentWrong'), type: 'error' })
      } finally {
        this.singleFarmAttachDialog.loading = false
        this.singleFarmAttachDialog.file = null
      }
    },
    async submitTrafficLightPanelMitigation(uploadedPaths) {
      if (!Array.isArray(uploadedPaths) || !uploadedPaths.length || !this.trafficLightItem) return

      this.trafficLightMitigationSubmitting = true
      try {
        const farmItem = this.trafficLightItem
        const paths = uploadedPaths.filter(Boolean)
        if (!paths.length) throw new Error('No uploaded mitigation files found')

        const reportProductionPlaceId = this.getReportProductionPlaceId(farmItem)
        const response = await DeforestationService.productionPlaceRiskMitigation({
          id: reportProductionPlaceId,
          eudr_deforestation_status: this.mapTrafficLightToMitigationStatus(farmItem),
          risk_mitigation_comment: '',
          files: paths,
          mitigation_source_type: DDS_MITIGATION_SOURCE_TYPE.LOCAL_DB_COMPLIANCE,
        })
        throwOnGraphqlErrors(response, 'Failed to submit mitigation evidence')

        await this.getProductionPlaces()
        this.refreshTrafficLightDialogItem(farmItem.id)
        this.$notify({
          text: 'Mitigation evidence submitted to PTSI successfully.',
          type: 'success',
        })
      } catch (error) {
        console.error('Failed to submit mitigation evidence', error)
        this.$notify({
          text: error?.message || this.$t('somethingWentWrong'),
          type: 'error',
        })
      } finally {
        this.trafficLightMitigationSubmitting = false
      }
    },
    async removeTrafficLightMitigationDoc(file) {
      if (!this.trafficLightItem || !this.canRemoveMitigationFile(file)) return

      const confirmed = window.confirm('Are you sure you want to remove this mitigation document?')
      if (!confirmed) return

      this.trafficLightMitigationSubmitting = true
      try {
        const farmItem = this.trafficLightItem
        if (file.isManualMitigation) {
          await DeforestationService.removeManuallyMitigatedFile({ fileId: Number(file.id) })
        } else {
          await DeforestationService.removeRiskMitigationFile({
            productionPlaceId: Number(farmItem.id),
            fileId: Number(file.id),
          })
        }

        await this.getProductionPlaces()
        this.refreshTrafficLightDialogItem(farmItem.id)
        this.$notify({ text: 'Mitigation document removed successfully.', type: 'success' })
      } catch (error) {
        console.error('Failed to remove mitigation document', error)
        this.$notify({ text: error?.message || this.$t('somethingWentWrong'), type: 'error' })
      } finally {
        this.trafficLightMitigationSubmitting = false
      }
    },
    openYellowWarning(item) {
      this.yellowWarningDialog = { open: true, farmItem: item }
    },
    async submitAttachDoc() {
      if (!this.attachDocDialog.file) return
      this.attachDocDialog.loading = true
      try {
        if (!this.trafficLightSummary) {
          await this.refreshTrafficLightSummary()
        }
        const scopes = this.attachDocDialog.selectedScopes.length
          ? this.attachDocDialog.selectedScopes
          : [
            ...(this.lightRedFarmCount > 0 ? ['lightRed'] : []),
            ...(this.yellowFarmCount > 0 ? ['yellow'] : []),
          ]
        const includeHighRisk = scopes.includes('lightRed')
        const includeMediumRisk = scopes.includes('yellow')
        if (!includeHighRisk && !includeMediumRisk) {
          this.$notify({ text: 'No farms selected for mitigation upload.', type: 'warning' })
          return
        }

        const filePath = await this.uploadMitigationFile(this.attachDocDialog.file)
        if (!filePath) throw new Error('Mitigation file upload failed')

        const response = await DeforestationService.bulkTrafficLightRiskMitigation({
          due_diligence_report_id: this.diligenceId,
          risk_mitigation_comment: '',
          file: filePath,
          include_high_risk: includeHighRisk,
          include_medium_risk: includeMediumRisk,
          mitigation_source_type: DDS_MITIGATION_SOURCE_TYPE.LOCAL_DB_COMPLIANCE,
        })
        throwOnGraphqlErrors(response, 'Failed to upload mitigation documents')
        const result = response?.data?.bulkTrafficLightRiskMitigation
        if (!result?.success) {
          throw new Error(result?.message || 'Failed to upload mitigation documents')
        }

        await this.getProductionPlaces()
        await this.refreshTrafficLightSummary()
        this.attachDocDialog.open = false
        this.$notify({ text: result?.message || 'Mitigation document uploaded successfully.', type: 'success' })
      } catch (error) {
        console.error('Failed to upload mitigation documents', error)
        this.$notify({ text: error?.message || this.$t('somethingWentWrong'), type: 'error' })
      } finally {
        this.attachDocDialog.loading = false
        this.attachDocDialog.file = null
      }
    },
    async confirmTrafficLightNextStep() {
      if (!this.hasPendingTrafficLightIssues) {
        return true
      }
      return new Promise(resolve => {
        this.nextConfirmResolve = resolve
        this.nextConfirmDialog = true
      })
    },
    confirmNextStep() {
      this.nextConfirmDialog = false
      if (this.nextConfirmResolve) {
        this.nextConfirmResolve(this.canProceedFromTrafficLightStep)
      }
    },
    cancelNextStep() {
      this.nextConfirmDialog = false
      if (this.nextConfirmResolve) this.nextConfirmResolve(false)
    },
    openMitigationFromProceedDialog() {
      this.nextConfirmDialog = false
      if (this.nextConfirmResolve) {
        this.nextConfirmResolve(false)
        this.nextConfirmResolve = null
      }
      this.openAttachDocDialog()
    },
    proceedWithoutMitigation() {
      this.nextConfirmDialog = false
      if (this.nextConfirmResolve) {
        this.nextConfirmResolve(true)
        this.nextConfirmResolve = null
      }
    },
    cancelAndCreateNewDds() {
      this.cancelNextStep()
      this.$store.dispatch('SET_DDS_TAB', { activeTab: 1 }).then(() => {
        const { href } = this.$router.resolve({ name: 'createDueDiligenceReports' })
        window.location.assign(href)
      })
    },
  },
}
